import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  LinearProgress,
  Typography,
  Box,
  Alert,
  Button,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { apiClient } from '../../services/api/authApi';
import { CalculationResultsDialog } from './CalculationResultsDialog';

interface CalculationProgressProps {
  open: boolean;
  onClose: () => void;
  calculationId?: string;
  fileId?: string;
  progress?: number;
  total?: number;
}

interface ProgressData {
  total: number;
  completed: number;
  percentComplete: number;
  status: 'processing' | 'completed' | 'failed';
  errors: string[];
  elapsedTime: number;
}

export const CalculationProgress: React.FC<CalculationProgressProps> = (props) => {
  const {
    open,
    onClose,
    calculationId,
    fileId,
  } = props;
  const [internalProgress, setInternalProgress] = useState<ProgressData | null>(null);

  // Use props if provided (mainly for testing), otherwise use internal state
  const progress = props.progress !== undefined && props.total !== undefined
    ? {
      total: props.total,
      completed: props.progress,
      percentComplete: Math.round((props.progress / props.total) * 100),
      status: (props.progress >= props.total ? 'completed' : 'processing') as 'processing' | 'completed' | 'failed',
      errors: [],
      elapsedTime: 0,
    }
    : internalProgress;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    // Only poll if we have calculationId and no manual progress is provided
    if (open && calculationId && props.progress === undefined) {
      const interval = setInterval(() => {
        fetchProgress();
      }, 2000); // Poll every 2 seconds

      // Initial fetch
      fetchProgress();

      return () => clearInterval(interval);
    }
  }, [open, calculationId, props.progress]);

  const fetchProgress = async () => {
    if (!calculationId) return;

    try {
      setLoading(true);
      const response = await apiClient.get(`/xml/calc/progress/${calculationId}`);

      if (response.data.success) {
        setInternalProgress(response.data.data);

        // Stop polling if completed or failed
        if (response.data.data.status === 'completed' || response.data.data.status === 'failed') {
          setLoading(false);
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch progress:', err);
      setError('Failed to fetch calculation progress');
      setLoading(false);
    }
  };

  const handleViewResults = () => {
    setShowResults(true);
  };

  const formatTime = (milliseconds: number): string => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getStatusIcon = () => {
    if (!progress) return <HourglassEmptyIcon />;

    switch (progress.status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      default:
        return <CircularProgress size={20} />;
    }
  };

  const getStatusColor = () => {
    if (!progress) return 'primary';

    switch (progress.status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'primary';
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            {getStatusIcon()}
            <Typography variant="h6">
              CO2 Calculation Progress
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {progress && (
            <Box>
              <Box sx={{ mb: 3 }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="textSecondary">
                    Progress: {progress.completed} / {progress.total} shipments
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {progress.percentComplete}%
                  </Typography>
                </Box>

                <LinearProgress
                  variant="determinate"
                  value={progress.percentComplete}
                  color={getStatusColor() as any}
                  sx={{ height: 10, borderRadius: 1 }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Status: <strong>{progress.status}</strong>
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Elapsed Time: {formatTime(progress.elapsedTime)}
                </Typography>
              </Box>

              {progress.errors && progress.errors.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Errors encountered:
                  </Typography>
                  <List dense>
                    {progress.errors.slice(0, 5).map((err, index) => (
                      <ListItem key={err}>
                        <ListItemText
                          primary={err}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  {progress.errors.length > 5 && (
                    <Typography variant="body2" color="textSecondary">
                      ...and {progress.errors.length - 5} more errors
                    </Typography>
                  )}
                </Alert>
              )}

              {progress.status === 'completed' && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Calculation completed successfully! All shipments have been processed.
                </Alert>
              )}

              {progress.status === 'failed' && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Calculation failed. Please check the errors and try again.
                </Alert>
              )}
            </Box>
          )}

          {!progress && !error && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}

          <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
            {progress?.status === 'completed' && fileId && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleViewResults}
              >
                View Results
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={loading && progress?.status === 'processing'}
            >
              {progress?.status === 'processing' ? 'Close (Processing...)' : 'Close'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Results Dialog */}
      {fileId && (
        <CalculationResultsDialog
          open={showResults}
          onClose={() => setShowResults(false)}
          fileId={fileId}
        />
      )}
    </>
  );
};